import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../flux/stores/hooks';
import { fetchApsConnection } from '../../flux/actions/apsThunks';
import { isAccLinkBroken } from '../../flux/facades/apsApi';
import { useApiAccessToken } from '../../utils/useApiAccessToken';
import './ApsLinkCell.css';

const LABELS = {
    idle: '…',
    loading: '…',
    notConnected: 'Not connected',
};

/**
 * The Autodesk column for one row of the projects list.
 *
 * Each row fetches its own connection, which means N requests for N projects.
 * That is a stopgap, accepted because the call only reads a local row and never
 * touches Autodesk, and because the alternative today is showing nothing. The
 * real fix belongs in the backend: the API design already anticipates a minimal
 * linked/not-linked boolean on the project representation, which would make
 * this one field of the list response instead of a request per row.
 *
 * Non-admins are never asked. The whole APS surface is org_admin only, so a
 * member would collect a 403 per row for a column they cannot act on.
 */
export const ApsLinkCell = ({ projectId, isAdmin }) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const aps = useAppSelector(state => state.projects.aps[projectId]);
    const status = aps?.status ?? 'idle';

    useEffect(() => {
        // `idle` means no entry yet and nothing in flight. Anything else is
        // either a fetch already running or an answer the detail page cached.
        if (!isAdmin || status !== 'idle') return;

        let cancelled = false;
        (async () => {
            try {
                const accessToken = await getAccessToken();
                if (!cancelled && accessToken) {
                    dispatch(fetchApsConnection({ accessToken, projectId }));
                }
            } catch (error) {
                console.error('Failed to acquire API token for the Autodesk column:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [dispatch, getAccessToken, isAdmin, projectId, status]);

    if (!isAdmin) return <span>-</span>;

    if (status === 'loaded' && aps?.connection) {
        const { connection } = aps;
        if (connection.status === 'revoked') {
            return <span className="pd-aps-pill pd-aps-pill-warn">Needs reconnect</span>;
        }
        const state = connection.acc_link?.state;
        if (state === 'unlinked') return <span className="pd-aps-pill">Not linked</span>;
        if (isAccLinkBroken(state)) {
            return <span className="pd-aps-pill pd-aps-pill-warn">Link broken</span>;
        }
        // `ok` and `unverified` both land here. Unverified means the check could
        // not run, which is not evidence against the pick, so a list cell says
        // the same thing for both rather than casting doubt it cannot support.
        return <span className="pd-aps-pill pd-aps-pill-ok">Linked</span>;
    }

    // A failed read is not worth shouting about in a list cell. The detail page
    // reports it properly.
    if (status === 'error') return <span>-</span>;

    return <span>{LABELS[status] ?? '-'}</span>;
};
